import React from 'react';
import { useQueryClient } from 'react-query';

import cacheKeys from 'Core/hooks/queries/cacheKeys';
import { formatCurrency } from 'Core/utils/numberFormat';
import { generateURL } from 'Core/utils/utils';

import { mixedDownPaymentCheckoutPath } from 'Modules/fortiva/routes';

const WalletIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M19 7V5a2 2 0 0 0-2-2H5a2 2 0 0 0 0 4h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5" />
    <path d="M16 12h.01" />
  </svg>
);

const SpinnerIcon = () => (
  <svg
    className="fs-spin"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

export default function FinancingPendingDownPayment({ amount = 0, setRefetchInterval }) {
  const queryClient = useQueryClient();
  const [isCollectPaymentLoading, setIsCollectPaymentLoading] = React.useState(false);

  // Same collect-payment behavior as the finalize screen, but without the
  // update-offer call — the offer is already finalized at this point, so we
  // only open the down-payment checkout in a new tab.
  const onCollectPayment = () => {
    setIsCollectPaymentLoading(true);

    const url = generateURL({ url: mixedDownPaymentCheckoutPath });
    setRefetchInterval?.(5000);
    queryClient.invalidateQueries(cacheKeys.financingNextStep);

    setTimeout(() => setIsCollectPaymentLoading(false), 5 * 60 * 1000);
    window.open(url, '_blank')?.focus();
  };

  return (
    <div className="fs-warning-block" data-testid="pending-down-payment">
      <header className="fs-section__head fs-section__head--sub">
        <span className="fs-section__icon" aria-hidden="true">
          <WalletIcon />
        </span>
        <div className="fs-section__heading">
          <span className="fs-eyebrow">Action Required</span>
          <h3 className="fs-section__title">Down Payment Pending</h3>
        </div>
      </header>
      <p className="fs-section__paragraph">
        A down payment of{' '}
        <strong data-testid="value-pending-down-payment">
          {formatCurrency(amount, { withDecimals: true })}
        </strong>{' '}
        still needs to be collected to complete this transaction.
      </p>
      <button
        type="button"
        className="fs-collect-btn"
        onClick={onCollectPayment}
        disabled={isCollectPaymentLoading}
        data-testid="btn-collect-down-payment"
      >
        {isCollectPaymentLoading && <SpinnerIcon />}
        Collect Payment
      </button>
    </div>
  );
}
